import React, { ReactNode, useState, useEffect } from 'react';
import { VStack, HStack, Box } from 'native-base';
import { i18nFn, Plan, useTheme } from '@springbig/shared';
import { Caption, H2, H3, H4 } from '~/shared/Typography';
import { Button, Card, CheckBox, } from '~/shared/Theme';

export type BillingSchedule = 'monthly' | 'yearly';

type SubscribeFormProps = {
    children: ReactNode;
    plan: Plan;
    onUpdateBillingSchedule: (value: BillingSchedule) => void;
    isLoading: boolean;
    t: i18nFn;
};

const SubscribeForm = ({
    children,
    plan,
    onUpdateBillingSchedule,
    isLoading,
    t
}: SubscribeFormProps) => {

    const {
        primary,
        field_background,
        field_color,
        field_border,
    } = useTheme();

    const [isMonthly, setIsMonthly] =
        useState<boolean>(true);

    const [isYearly, setIsYearly] =
        useState<boolean>(false);

    useEffect(() => {

        onUpdateBillingSchedule(isMonthly ? 'monthly' : 'yearly');
    }, [isMonthly, isYearly, onUpdateBillingSchedule]);

    useEffect(() => {

        if (plan.billing_schedule === 'yearly') {
            setIsMonthly(false);
            setIsYearly(true);
        }
    }, [plan]);

    return (
        <VStack space={4} px={4} pt={4}>
            { plan.billing_schedule === 'both' &&
                <VStack space={4}>
                    <H2 textAlign={'center'} text={'credit_card.choose_plan'} />
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        onPress={() => {

                            setIsMonthly(true); setIsYearly(false);
                        }}
                    >
                        <Card
                            borderColor={isMonthly ? primary : 'transparent'}
                            borderWidth={2}
                            hasShadow={isMonthly}
                        >
                            <HStack space={4} justifyContent={'space-between'} alignItems={'flex-start'}>
                                <VStack space={2} flex={1}>
                                    <Box>
                                        <H3>{ t('credit_card.monthly_title') }</H3>
                                        <H4 fontSize={20}>
                                            { t('credit_card.monthly_price', [plan.recurring_amount.toFixed(2)]) }
                                        </H4>
                                    </Box>
                                    { plan.trial_period > 0 &&
                                        <Box
                                            backgroundColor={'hsl(120, 40%, 80%)'}
                                            paddingX={1}
                                            borderRadius={3} w={'fit-content'}
                                        >
                                            <Caption
                                                fontWeight={'700'}
                                                color={'hsl(120, 40%, 40%)'}
                                                fontSize={12}
                                            >
                                                { t('credit_card.free_trial', [plan.trial_period]) }
                                            </Caption>
                                        </Box>
                                    }
                                    <Caption opacity={.5}>
                                        { t('credit_card.monthly_summary', [plan.recurring_amount.toFixed(2)]) }
                                    </Caption>
                                </VStack>
                                <CheckBox
                                    isChecked={isMonthly}
                                    value={isMonthly.toString()}
                                    isDisabled={isLoading}
                                    onChange={(value) => {

                                        if (value) {
                                            setIsMonthly(value);
                                            setIsYearly(!value);
                                        }
                                    }}
                                />
                            </HStack>
                        </Card>
                    </Button>

                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        onPress={() => {

                            setIsMonthly(false); setIsYearly(true);
                        }}
                    >
                        <Card
                            borderColor={isYearly ? primary : 'transparent'}
                            borderWidth={2}
                            hasShadow={isYearly}
                        >
                            <HStack space={4} justifyContent={'space-between'} alignItems={'flex-start'}>
                                <VStack space={2} flex={1}>
                                    <Box>
                                        <H3>{ t('credit_card.yearly_title') }</H3>
                                        <H4 fontSize={20}>
                                            { t('credit_card.yearly_price', [plan.yearly_recurring_amount.toFixed(2)]) }
                                        </H4>
                                    </Box>
                                    { plan.trial_period > 0 &&
                                    <Box
                                        backgroundColor={'hsl(120, 40%, 80%)'}
                                        paddingX={1}
                                        borderRadius={3} w={'fit-content'}
                                    >
                                        <Caption
                                            fontWeight={'700'}
                                            color={'hsl(120, 40%, 40%)'}
                                            fontSize={12}
                                        >
                                            { t('credit_card.free_trial', [plan.trial_period]) }
                                        </Caption>
                                    </Box>
                                    }
                                    <Caption opacity={.5}>
                                        { t('credit_card.yearly_summary', [plan.yearly_recurring_amount.toFixed(2)]) }
                                    </Caption>
                                </VStack>
                                <CheckBox
                                    flex={1}
                                    isChecked={isYearly}
                                    value={isYearly.toString()}
                                    isDisabled={isLoading}
                                    onChange={(value) => {

                                        if (value) {
                                            setIsMonthly(!value);
                                            setIsYearly(value);
                                        }
                                    }}
                                />
                            </HStack>
                        </Card>
                    </Button>
                </VStack>
            }

            <Box>
                <H2 textAlign={'center'}>
                    { t('credit_card.add_card') }
                </H2>

                { children }
            </Box>
        </VStack>
    );
};

export default SubscribeForm;
